import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { UUID_REGEX } from '../utils/slug';

type Club = {
  id: string;
  slug: string;
  name: string;
  leader_id: string | null;
};

type RunRecord = {
  id: string;
  day_of_week: string;
  start_time: string;
  location_address: string | null;
  distance: string | null;
  notes: string | null;
  route_id: string | null;
};

type RouteOption = {
  id: string;
  name: string;
  route_type: string | null;
  surface: string | null;
  distance_miles: number | null;
  elevation_gain_ft: number | null;
};

// One exception to a standing weekly slot on a specific date. A date with no
// row is the slot exactly as written on `runs`.
type InstanceStatus = 'canceled' | 'moved' | 'special';

type RunInstance = {
  id: string;
  run_id: string;
  club_id: string;
  run_date: string;
  status: InstanceStatus;
  title: string | null;
  start_time_override: string | null;
  location_address_override: string | null;
  distance_override: string | null;
  notes_override: string | null;
  route_id: string | null;
  created_at: string;
};

type InstanceForm = {
  run_id: string;
  run_date: string;
  status: InstanceStatus;
  title: string;
  start_time_override: string;
  location_address_override: string;
  distance_override: string;
  notes_override: string;
  route_id: string;
};

const emptyForm: InstanceForm = {
  run_id: '',
  run_date: '',
  status: 'canceled',
  title: '',
  start_time_override: '',
  location_address_override: '',
  distance_override: '',
  notes_override: '',
  route_id: '',
};

function formatRouteMeta(route: RouteOption) {
  const parts: string[] = [];
  if (route.route_type) parts.push(route.route_type);
  if (route.surface) parts.push(route.surface);
  if (route.distance_miles != null) parts.push(`${route.distance_miles} mi`);
  if (route.elevation_gain_ft != null) {
    parts.push(`${route.elevation_gain_ft.toLocaleString()} ft`);
  }
  return parts.join(' · ');
}

function formatRunLabel(run: RunRecord) {
  const parts = [`${run.day_of_week}`, run.start_time];
  if (run.location_address) parts.push(run.location_address);
  return parts.join(' · ');
}

export default function ManageRunInstances() {
  const { id } = useParams();
  const { user, leader, canManageClub, loading: authLoading } = useAuth();

  const [club, setClub] = useState<Club | null>(null);
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [routes, setRoutes] = useState<RouteOption[]>([]);
  const [instances, setInstances] = useState<RunInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);

  const [form, setForm] = useState<InstanceForm>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    async function loadPage() {
      if (!id || !leader) return;

      setLoading(true);
      setErrorMessage(null);

      const isUuid = UUID_REGEX.test(id);
      const { data: clubData, error: clubError } = await (
        isUuid
          ? supabase.from('clubs').select('id, slug, name, leader_id').eq('id', id).single()
          : supabase.from('clubs').select('id, slug, name, leader_id').eq('slug', id).single()
      );

      const clubUuid = (clubData as Club | null)?.id ?? id;

      const [
        { data: runsData, error: runsError },
        { data: routesData, error: routesError },
        { data: instancesData, error: instancesError },
      ] = await Promise.all([
        supabase
          .from('runs')
          .select('id, day_of_week, start_time, location_address, distance, notes, route_id')
          .eq('club_id', clubUuid)
          .order('day_of_week', { ascending: true })
          .order('start_time', { ascending: true }),
        supabase
          .from('routes')
          .select('id, name, route_type, surface, distance_miles, elevation_gain_ft')
          .eq('club_id', clubUuid)
          .order('created_at', { ascending: false }),
        supabase
          .from('run_instances')
          .select(`
            id,
            run_id,
            club_id,
            run_date,
            status,
            title,
            start_time_override,
            location_address_override,
            distance_override,
            notes_override,
            route_id,
            created_at
          `)
          .eq('club_id', clubUuid)
          .order('run_date', { ascending: true }),
      ]);

      if (clubError || !clubData) {
        setErrorMessage(clubError?.message || 'Unable to load club.');
        setLoading(false);
        return;
      }

      const typedClub = clubData as Club;
      // club_leaders membership is the authority (admins manage everything)
      const canManage = canManageClub(typedClub.id);

      setClub(typedClub);
      setAllowed(canManage);

      if (!canManage) {
        setLoading(false);
        return;
      }

      if (runsError) {
        setErrorMessage(runsError.message);
      } else {
        setRuns((runsData || []) as RunRecord[]);
      }

      if (routesError) {
        setErrorMessage(routesError.message);
      } else {
        setRoutes((routesData || []) as RouteOption[]);
      }

      if (instancesError) {
        setErrorMessage(instancesError.message);
      } else {
        setInstances((instancesData || []) as RunInstance[]);
      }

      setLoading(false);
    }

    if (!authLoading) {
      loadPage();
    }
  }, [authLoading, id, leader, canManageClub]);

  const pageTitle = useMemo(() => {
    if (!club) return 'Run Overrides';
    return `Run Overrides · ${club.name}`;
  }, [club]);

  function updateField<K extends keyof InstanceForm>(key: K, value: InstanceForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function resetForm() {
    setForm(emptyForm);
    setEditingId(null);
  }

  function startEdit(instance: RunInstance) {
    setEditingId(instance.id);
    setMessage(null);
    setErrorMessage(null);

    setForm({
      run_id: instance.run_id,
      run_date: instance.run_date,
      status: instance.status,
      title: instance.title || '',
      start_time_override: instance.start_time_override || '',
      location_address_override: instance.location_address_override || '',
      distance_override: instance.distance_override || '',
      notes_override: instance.notes_override || '',
      route_id: instance.route_id || '',
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function refreshInstances() {
    if (!club?.id) return;

    const { data, error } = await supabase
      .from('run_instances')
      .select(`
        id,
        run_id,
        club_id,
        run_date,
        status,
        title,
        start_time_override,
        location_address_override,
        distance_override,
        notes_override,
        route_id,
        created_at
      `)
      .eq('club_id', club.id)
      .order('run_date', { ascending: true });

    if (error) {
      setErrorMessage(error.message);
      return;
    }

    setInstances((data || []) as RunInstance[]);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!id) return;

    setSaving(true);
    setMessage(null);
    setErrorMessage(null);

    try {
      if (!form.run_id) throw new Error('Please select a recurring run.');
      if (!form.run_date) throw new Error('Please select a run date.');

      // club_id is set from the parent run by a database trigger.
      const payload = {
        run_id: form.run_id,
        club_id: club!.id,
        run_date: form.run_date,
        status: form.status,
        title: form.title.trim() || null,
        start_time_override: form.start_time_override.trim() || null,
        location_address_override: form.location_address_override.trim() || null,
        distance_override: form.distance_override.trim() || null,
        notes_override: form.notes_override.trim() || null,
        route_id: form.route_id || null,
      };

      if (editingId) {
        const { error } = await supabase
          .from('run_instances')
          .update(payload)
          .eq('id', editingId);

        if (error) throw new Error(error.message);
        setMessage('Run override updated.');
      } else {
        const { error } = await supabase
          .from('run_instances')
          .insert({ ...payload, created_by: leader?.id ?? null });

        if (error) throw new Error(error.message);
        setMessage('Run override created.');
      }

      await refreshInstances();
      resetForm();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to save override.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(instanceId: string) {
    setDeletingId(instanceId);
    setMessage(null);
    setErrorMessage(null);

    const { error } = await supabase.from('run_instances').delete().eq('id', instanceId);

    if (error) {
      setErrorMessage(error.message);
      setDeletingId(null);
      return;
    }

    setInstances((prev) => prev.filter((instance) => instance.id !== instanceId));
    setMessage('Run override deleted.');

    if (editingId === instanceId) {
      resetForm();
    }

    setDeletingId(null);
  }

  const runLookup = useMemo(() => {
    return new Map(runs.map((run) => [run.id, run]));
  }, [runs]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 px-6 py-12">
        <div className="mx-auto max-w-5xl rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
          <div className="h-8 w-56 animate-pulse rounded bg-black/10" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!leader) {
    return (
      <div className="min-h-screen bg-neutral-50 px-6 py-12">
        <div className="mx-auto max-w-3xl rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-black">Leader access required</h1>
          <p className="mt-3 text-black/65">
            Your account is signed in, but it is not linked to a run leader record.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 px-6 py-12">
        <div className="mx-auto max-w-5xl rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
          <div className="h-8 w-56 animate-pulse rounded bg-black/10" />
        </div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen bg-neutral-50 px-6 py-12">
        <div className="mx-auto max-w-3xl rounded-3xl border border-black/10 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-black">Access denied</h1>
          <p className="mt-3 text-black/65">
            You do not have permission to manage overrides for this club.
          </p>
          <div className="mt-6">
            <Link
              to="/explore"
              className="rounded-2xl bg-black px-5 py-3 text-sm font-medium text-white transition hover:opacity-90"
            >
              Back to Explore
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pb-16">
      <div className="mx-auto max-w-6xl px-6 pt-8">
        <div className="overflow-hidden rounded-[2rem] border border-black/8 bg-white shadow-sm">
          <div className="bg-gradient-to-br from-black to-neutral-700 px-8 py-10 text-white">
            <p className="text-xs font-medium uppercase tracking-[0.22em] text-white/70">
              Schedule Exceptions
            </p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight md:text-5xl">
              {pageTitle}
            </h1>
            <p className="mt-3 max-w-2xl text-base text-white/80">
              Your weekly schedule stays as written. Add an exception only for the dates that differ.
            </p>

            {club && (
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/clubs/${club.slug}`}
                  className="rounded-2xl bg-white px-4 py-2.5 text-sm font-medium text-black transition hover:opacity-90"
                >
                  View Club
                </Link>
                <Link
                  to={`/clubs/${club.slug}/edit`}
                  className="rounded-2xl border border-white/25 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white hover:text-black"
                >
                  Edit Club
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="space-y-6">
            <div className="rounded-3xl border border-black/10 bg-white p-6 shadow-sm md:p-8">
              <h2 className="text-xl font-semibold text-black">
                {editingId ? 'Edit override' : 'Create override'}
              </h2>
              <p className="mt-2 text-sm text-black/60">
                Pick the weekly run and the date. Leave a field blank to keep what the weekly
                schedule says. To move a run to a different day, cancel the original date and add a
                &ldquo;moved&rdquo; exception on the new date.
              </p>

              {message && (
                <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {message}
                </div>
              )}

              {errorMessage && (
                <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-black">
                    Recurring run
                  </label>
                  <select
                    value={form.run_id}
                    onChange={(e) => updateField('run_id', e.target.value)}
                    className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                  >
                    <option value="">Select a recurring run</option>
                    {runs.map((run) => (
                      <option key={run.id} value={run.id}>
                        {formatRunLabel(run)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black">Date</label>
                    <input
                      type="date"
                      value={form.run_date}
                      onChange={(e) => updateField('run_date', e.target.value)}
                      className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-black">Status</label>
                    <select
                      value={form.status}
                      onChange={(e) =>
                        updateField('status', e.target.value as InstanceForm['status'])
                      }
                      className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                    >
                      <option value="canceled">Canceled — no run this date</option>
                      <option value="moved">Moved — different time, place or route this week</option>
                      <option value="special">Special — one-off variation of this run</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-black">
                    Override title
                  </label>
                  <input
                    value={form.title}
                    onChange={(e) => updateField('title', e.target.value)}
                    className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                    placeholder="Sunrise Long Run"
                  />
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-black">
                      Start time override
                    </label>
                    <input
                      value={form.start_time_override}
                      onChange={(e) => updateField('start_time_override', e.target.value)}
                      className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                      placeholder="7:00 AM"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-black">
                      Distance override
                    </label>
                    <input
                      value={form.distance_override}
                      onChange={(e) => updateField('distance_override', e.target.value)}
                      className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                      placeholder="8 miles"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-black">
                    Location override
                  </label>
                  <input
                    value={form.location_address_override}
                    onChange={(e) => updateField('location_address_override', e.target.value)}
                    className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                    placeholder="Alternative trailhead or meetup point"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-black">
                    Route override
                  </label>
                  <select
                    value={form.route_id}
                    onChange={(e) => updateField('route_id', e.target.value)}
                    className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                  >
                    <option value="">No route override</option>
                    {routes.map((route) => (
                      <option key={route.id} value={route.id}>
                        {route.name}
                        {formatRouteMeta(route) ? ` — ${formatRouteMeta(route)}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-black">
                    Notes override
                  </label>
                  <textarea
                    value={form.notes_override}
                    onChange={(e) => updateField('notes_override', e.target.value)}
                    rows={4}
                    className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
                    placeholder="Explain what is different about this instance."
                  />
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-2xl bg-black px-5 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving
                      ? editingId
                        ? 'Saving...'
                        : 'Creating...'
                      : editingId
                      ? 'Save override'
                      : 'Create override'}
                  </button>

                  {editingId && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="rounded-2xl border border-black px-5 py-3 text-sm font-medium text-black transition hover:bg-black hover:text-white"
                    >
                      Cancel edit
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-black/10 bg-white p-6 shadow-sm md:p-8">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-black">Saved overrides</h2>
                  <p className="mt-2 text-sm text-black/60">
                    Date-specific changes layered on top of recurring runs.
                  </p>
                </div>

                <div className="rounded-full border border-black/10 bg-neutral-50 px-3 py-1.5 text-xs font-medium text-black/60">
                  {instances.length} overrides
                </div>
              </div>

              {instances.length === 0 ? (
                <div className="mt-6 rounded-2xl border border-dashed border-black/12 bg-neutral-50 px-5 py-8 text-sm text-black/55">
                  No overrides created yet.
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  {instances.map((instance) => {
                    const baseRun = runLookup.get(instance.run_id);

                    return (
                      <div
                        key={instance.id}
                        className="rounded-3xl border border-black/8 bg-neutral-50 p-5"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <h3 className="text-lg font-semibold text-black">
                              {instance.title || baseRun?.day_of_week || 'Run override'}
                            </h3>
                            <p className="mt-1 text-sm text-black/60">{instance.run_date}</p>
                            {baseRun && (
                              <p className="mt-2 text-xs uppercase tracking-[0.12em] text-black/40">
                                Base run · {formatRunLabel(baseRun)}
                              </p>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => startEdit(instance)}
                              className="rounded-xl border border-black px-3 py-2 text-xs font-medium text-black transition hover:bg-black hover:text-white"
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(instance.id)}
                              disabled={deletingId === instance.id}
                              className="rounded-xl border border-red-300 px-3 py-2 text-xs font-medium text-red-600 transition hover:bg-red-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {deletingId === instance.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          <span className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium capitalize text-black/70">
                            {instance.status}
                          </span>
                          {instance.start_time_override && (
                            <span className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-black/70">
                              {instance.start_time_override}
                            </span>
                          )}
                          {instance.distance_override && (
                            <span className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs font-medium text-black/70">
                              {instance.distance_override}
                            </span>
                          )}
                        </div>

                        {instance.location_address_override && (
                          <p className="mt-3 text-sm text-black/65">
                            <span className="font-medium text-black">Location:</span>{' '}
                            {instance.location_address_override}
                          </p>
                        )}

                        {instance.notes_override && (
                          <p className="mt-3 text-sm leading-7 text-black/65">
                            {instance.notes_override}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {club && (
              <div className="rounded-3xl border border-black/10 bg-white p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-black">What comes next</h2>
                <p className="mt-2 text-sm leading-6 text-black/60">
                  Today and the club page read these exceptions on top of the weekly schedule.
                  Runners see a canceled run disappear and a moved one flagged for that date.
                </p>

                <div className="mt-4 flex flex-wrap gap-3">
                  <Link
                    to={`/clubs/${club.slug}`}
                    className="rounded-2xl border border-black px-4 py-2.5 text-sm font-medium text-black transition hover:bg-black hover:text-white"
                  >
                    View Club
                  </Link>
                  <Link
                    to={`/clubs/${club.slug}/edit`}
                    className="rounded-2xl bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90"
                  >
                    Edit Club
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}